import { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { FaCircleExclamation } from "react-icons/fa6";
import MontacargasNavbar from "../components/MontacargasNavbar";
import { getLiftUser, clearLiftSession } from "../services/liftSession";
import { searchMaster } from "../services/liftApi";
import "./home.css";

// Guía master: 3 dígitos de prefijo + 8 de serie => XXX-XXXXXXXX
// (minlength/maxlength=12 en search.blade.php).
const AWB_DIGITS = 11;
const AWB_PATTERN = /^\d{3}-\d{8}$/;
const AWB_ERROR_ID = "montacargas-awb-error";

// Réplica de search.blade.php: solo dígitos (Numeros()) y a partir del 4°
// dígito se inserta el guión.
const formatAwb = (digits) => (digits.length > 3 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits);

// Equivalente a required + minlength/maxlength del PHP, con mensaje propio.
const validateAwb = (value) => {
  if (!value) return "Ingresa la guía master";
  if (!AWB_PATTERN.test(value)) return "La guía master debe tener 11 dígitos (000-00000000)";
  return "";
};

// Réplica de amcmontacargas-L8.8 (PWAController::index / SearchController::form):
// búsqueda de guía master, primera pantalla después del login. Si la guía
// existe pasa a "Carga insumo" (auditor); si no, muestra el mismo error que
// search.blade.php.
function MontacargasHome() {
  const navigate = useNavigate();
  const user = getLiftUser();
  const inputRef = useRef(null);
  const pendingCaretRef = useRef(null);
  const [awb, setAwb] = useState("");
  const [searching, setSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [inputInvalid, setInputInvalid] = useState(false);

  // Al reformatear, deja el cursor después del mismo número de dígitos que
  // tenía antes (si no, al editar a mitad de la guía salta al final).
  useLayoutEffect(() => {
    const digitsBeforeCaret = pendingCaretRef.current;
    const input = inputRef.current;
    if (digitsBeforeCaret === null || !input) return;
    pendingCaretRef.current = null;

    let position = 0;
    let seen = 0;
    while (position < awb.length && seen < digitsBeforeCaret) {
      if (/\d/.test(awb[position])) seen += 1;
      position += 1;
    }
    input.setSelectionRange(position, position);
  }, [awb]);

  // Sin maxLength en el input: se limpia y recorta aquí, así un pegado con
  // espacios o guiones ("139 1234 5678") no pierde dígitos.
  const handleAwbChange = (event) => {
    const { value, selectionStart } = event.target;
    const digits = value.replace(/\D/g, "").slice(0, AWB_DIGITS);

    pendingCaretRef.current =
      selectionStart === null ? null : value.slice(0, selectionStart).replace(/\D/g, "").length;
    setAwb(formatAwb(digits));
    setErrorMessage("");
    setInputInvalid(false);
  };

  const handleLogout = () => {
    clearLiftSession();
    navigate("/montacargas", { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (searching) return;

    setErrorMessage("");
    setInputInvalid(false);

    const validationError = validateAwb(awb);
    if (validationError) {
      setErrorMessage(validationError);
      setInputInvalid(true);
      inputRef.current?.focus();
      return;
    }

    setSearching(true);

    try {
      const response = await searchMaster({ awb });
      navigate(`/montacargas/carga-insumo/${encodeURIComponent(response.data.awb_master)}`);
    } catch (error) {
      // 400 = formato de guía inválido; 404 = la guía no existe / no es de
      // exportación (mensaje del PHP). Cualquier otro error (500, red) no se
      // muestra tal cual al usuario.
      const status = error.response?.status;
      if (status === 400 || status === 404) {
        setErrorMessage(
          error.response.data?.message ||
            (status === 404 ? "Esta guia no es de exportación, favor de validar" : validateAwb(awb)),
        );
        setInputInvalid(true);
      } else if (error.response?.status !== 401) {
        setErrorMessage("No se pudo buscar la guía, intenta de nuevo.");
      }
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="mc-page">
      <MontacargasNavbar title="Búsqueda Master">
        <button type="button" className="mc-navbar-btn" onClick={handleLogout}>
          Cerrar sesión
        </button>
      </MontacargasNavbar>

      <main className="mc-page__content">
        <h2 className="mc-page__welcome">Bienvenid@{user?.name ? `, ${user.name}` : ""}</h2>

        <form className="mc-search" onSubmit={handleSubmit} noValidate>
          <div className="mc-search__field">
            <label className="mc-search__label" htmlFor="montacargas-awb">
              Guia master
            </label>
            <input
              id="montacargas-awb"
              type="text"
              ref={inputRef}
              inputMode="numeric"
              pattern="[0-9]{3}-[0-9]{8}"
              enterKeyHint="search"
              className={`form-control mc-search__input${inputInvalid ? " is-invalid" : ""}`}
              placeholder="Ingresar guía master"
              value={awb}
              onChange={handleAwbChange}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              aria-invalid={inputInvalid}
              aria-describedby={errorMessage ? AWB_ERROR_ID : undefined}
              disabled={searching}
              required
            />
          </div>

          {errorMessage && (
            <div id={AWB_ERROR_ID} className="mc-alert mc-search__alert" role="alert">
              <FaCircleExclamation aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          <button type="submit" className="mc-btn-primary" disabled={searching}>
            {searching ? (
              <>
                <Spinner animation="border" size="sm" />
                Buscando...
              </>
            ) : (
              "Buscar"
            )}
          </button>
        </form>
      </main>
    </div>
  );
}

export default MontacargasHome;
